/**
 */
import { useState, useEffect, useCallback } from 'react';
import { Bell, BellSlash, BellRinging, Check, X } from '@phosphor-icons/react';
import { logger } from '@/utils/logger';

const API = process.env.REACT_APP_BACKEND_URL;

// Solicitar permiso para notificaciones
const requestNotificationPermission = async () => {
  if (!('Notification' in window)) {
    logger.log('Este navegador no soporta notificaciones');
    return false;
  }
  
  if (Notification.permission === 'granted') {
    return true;
  }
  
  if (Notification.permission !== 'denied') {
    const permission = await Notification.requestPermission();
    return permission === 'granted';
  }
  
  return false;
};

// Registrar service worker
const registerServiceWorker = async () => {
  if ('serviceWorker' in navigator) {
    try {
      const registration = await navigator.serviceWorker.register('/service-worker.js');
      logger.log('Service Worker registrado:', registration.scope);
      return registration;
    } catch (error) {
      logger.error('Error registrando Service Worker:', error);
      return null;
    }
  }
  return null;
};

// Suscribirse a notificaciones push
const subscribeToPush = async (registration, vapidPublicKey) => {
  try {
    // Verificar si ya hay una suscripción
    let subscription = await registration.pushManager.getSubscription();
    
    if (!subscription && vapidPublicKey) {
      // Convertir VAPID key de base64 a Uint8Array
      const urlBase64ToUint8Array = (base64String) => {
        const padding = '='.repeat((4 - base64String.length % 4) % 4);
        const base64 = (base64String + padding)
          .replace(/-/g, '+')
          .replace(/_/g, '/');
        const rawData = window.atob(base64);
        const outputArray = new Uint8Array(rawData.length);
        for (let i = 0; i < rawData.length; ++i) {
          outputArray[i] = rawData.charCodeAt(i);
        }
        return outputArray;
      };
      
      // Crear nueva suscripción con VAPID key
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
      });
    }
    
    return subscription;
  } catch (error) {
    logger.error('Error suscribiendo a push:', error);
    return null;
  }
};


const NotificationManager = ({ isOpen, onClose }) => {
  const [pushEnabled, setPushEnabled] = useState(false);
  const [emailEnabled, setEmailEnabled] = useState(false);
  const [email, setEmail] = useState('');
  const [frequency, setFrequency] = useState(60);
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);
  const [swRegistration, setSwRegistration] = useState(null);
  const [vapidKey, setVapidKey] = useState(null);
  const [vapidEnabled, setVapidEnabled] = useState(false);

  // Cargar VAPID key del servidor
  useEffect(() => {
    const loadVapidKey = async () => {
      try {
        const response = await fetch(`${API}/api/notifications/vapid-key`);
        if (response.ok) {
          const data = await response.json();
          setVapidEnabled(data.vapid_enabled);
          setVapidKey(data.public_key);
        }
      } catch (error) {
        logger.error('Error loading VAPID key:', error);
      }
    };
    loadVapidKey();
  }, []);

  // Cargar estado actual
  useEffect(() => {
    const loadStatus = async () => {
      try {
        
        
        const response = await fetch(`${API}/api/notifications/status`, {
        });
        
        if (response.ok) {
          const data = await response.json();
          setPushEnabled(data.push_enabled);
          setEmailEnabled(data.email_enabled);
          setEmail(data.email || '');
          setFrequency(data.frequency_minutes || 60);
        }
      } catch (error) {
        logger.error('Error loading notification status:', error);
      }
    };
    
    if (isOpen) {
      loadStatus();
    }
  }, [isOpen]);

  // Registrar service worker al montar
  useEffect(() => {
    const setupSW = async () => {
      const registration = await registerServiceWorker();
      if (registration) {
        setSwRegistration(registration);
      }
    };
    setupSW();
  }, []);

  // Habilitar/deshabilitar notificaciones push
  const togglePush = useCallback(async () => {
    setLoading(true);
    try {
      
      
      if (!pushEnabled) {
        // Verificar VAPID
        if (!vapidEnabled || !vapidKey) {
          alert('Push notifications no están configuradas en el servidor');
          setLoading(false);
          return;
        }
        
        // Habilitar
        const hasPermission = await requestNotificationPermission();
        if (!hasPermission) {
          alert('Necesitas permitir las notificaciones en tu navegador');
          setLoading(false);
          return;
        }
        
        if (swRegistration) {
          const subscription = await subscribeToPush(swRegistration, vapidKey);
          if (subscription) {
            const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
            await fetch(`${API}/api/notifications/subscribe`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`
              },
              body: JSON.stringify({
                endpoint: subscription.endpoint,
                keys: subscription.toJSON().keys || {}
              })
            });
            setPushEnabled(true);
            
            // Mostrar notificación de prueba
            new Notification('Notificaciones activadas', {
              body: 'Recibirás alertas de noticias importantes del mercado',
              icon: '/logo192.png'
            });
          }
        }
      } else {
        // Deshabilitar
        await fetch(`${API}/api/notifications/unsubscribe`, {
          method: 'DELETE',
        });
        setPushEnabled(false);
      }
    } catch (error) {
      logger.error('Error toggling push:', error);
    }
    setLoading(false);
  }, [pushEnabled, swRegistration, vapidEnabled, vapidKey]);

  // Guardar configuración de email
  const saveEmailSettings = async () => {
    setLoading(true);
    setSaved(false);
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
      await fetch(`${API}/api/settings/email-alerts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          enabled: emailEnabled,
          email: email,
          portfolio_only: false,
          high_impact_only: true,
          frequency_minutes: frequency
        })
      });
      
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (error) {
      logger.error('Error saving email settings:', error);
    }
    setLoading(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl max-w-md w-full shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-zinc-800">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gold-500/20 rounded-lg">
              <BellRinging size={24} className="text-gold-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">Notificaciones</h2>
              <p className="text-xs text-zinc-500">Alertas de noticias del mercado</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-zinc-800 rounded-lg transition-colors"
          >
            <X size={20} className="text-zinc-400" />
          </button>
        </div>

        <div className="p-4 space-y-6">
          {/* Push Notifications */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {pushEnabled ? (
                  <Bell size={20} className="text-green-400" />
                ) : (
                  <BellSlash size={20} className="text-zinc-500" />
                )}
                <span className="text-sm font-medium text-white">Notificaciones Push</span>
              </div>
              <button
                onClick={togglePush}
                disabled={loading}
                className={`relative w-12 h-6 rounded-full transition-colors ${
                  pushEnabled ? 'bg-green-500' : 'bg-zinc-700'
                }`}
              >
                <span
                  className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                    pushEnabled ? 'left-7' : 'left-1'
                  }`}
                />
              </button>
            </div>
            <p className="text-xs text-zinc-500">
              Recibe alertas instantáneas cuando hay noticias importantes
            </p>
          </div>

          <div className="border-t border-zinc-800" />

          {/* Email Notifications */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-white">Alertas por Email</span>
              <button
                onClick={() => setEmailEnabled(!emailEnabled)}
                className={`relative w-12 h-6 rounded-full transition-colors ${
                  emailEnabled ? 'bg-gold-500' : 'bg-zinc-700'
                }`}
              >
                <span
                  className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform ${
                    emailEnabled ? 'left-7' : 'left-1'
                  }`}
                />
              </button>
            </div>

            {emailEnabled && (
              <div className="space-y-3 animate-in fade-in duration-200">
                <div>
                  <label className="text-xs text-zinc-500 block mb-1">Email</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="tu@email.com"
                    className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-sm text-white placeholder-zinc-500 focus:border-gold-500 focus:outline-none"
                  />
                </div>

                <div>
                  <label className="text-xs text-zinc-500 block mb-1">Frecuencia</label>
                  <select
                    value={frequency}
                    onChange={(e) => setFrequency(Number(e.target.value))}
                    className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-sm text-white focus:border-gold-500 focus:outline-none"
                  >
                    <option value={30}>Cada 30 minutos</option>
                    <option value={60}>Cada hora</option>
                    <option value={120}>Cada 2 horas</option>
                    <option value={240}>Cada 4 horas</option>
                    <option value={480}>Cada 8 horas</option>
                  </select>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-zinc-800 flex items-center justify-between">
          <p className="text-xs text-zinc-500">
            Solo recibirás noticias de alto impacto
          </p>
          <button
            onClick={saveEmailSettings}
            disabled={loading}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
              saved
                ? 'bg-green-500 text-white'
                : 'bg-gold-500 hover:bg-gold-400 text-black'
            }`}
          >
            {saved ? (
              <>
                <Check size={16} /> Guardado
              </>
            ) : loading ? (
              'Guardando...'
            ) : (
              'Guardar'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NotificationManager;
